import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Button from '../../../../components/ui/Button';
import { API_ENDPOINTS } from '../../../../utils/config';
import { 
  FileText, 
  Plus, 
  Search, 
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  DollarSign,
  Calendar,
  User,
  MoreHorizontal,
  Send,
  CreditCard,
  TrendingUp,
  BarChart3,
  X
} from 'lucide-react';
import InvoiceModal from './InvoiceModal';
import InvoiceDetailsModal from './InvoiceDetailsModal';

const Invoices = ({ variant = 'invoices' }) => {
  const isEstimates = variant === 'estimates';
  const noun = isEstimates ? 'estimate' : 'invoice';
  const nounPlural = isEstimates ? 'estimates' : 'invoices';
  const nounTitle = isEstimates ? 'Estimate' : 'Invoice';

  const getDocumentType = (row) => row?.document_type ?? row?.documentType;

  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [modalMode, setModalMode] = useState('add');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [prevPage, setPrevPage] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
  };

  const fetchInvoices = async (page = 1) => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterType !== 'all') params.append('type', filterType);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('ordering', '-invoice_date');

      const listUrl = isEstimates ? API_ENDPOINTS.ESTIMATES_LIST : API_ENDPOINTS.INVOICES_LIST;
      const response = await axios.get(
        `${listUrl}?${params.toString()}`,
        { headers: getAuthHeaders() }
      );

      const data = response.data;
      // Normalize both paginated objects and plain array responses
      const normalizedInvoices = Array.isArray(data) ? data : (data.results || []);
      const rows = isEstimates
        ? normalizedInvoices
        : normalizedInvoices.filter((inv) => getDocumentType(inv) !== 'estimate');
      const normalizedCount = Array.isArray(data) ? rows.length : (data.count || rows.length || 0);
      setInvoices(rows);
      setTotalCount(normalizedCount);
      setNextPage(Array.isArray(data) ? null : data.next);
      setPrevPage(Array.isArray(data) ? null : data.previous);
      setCurrentPage(page);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      `Failed to fetch ${nounPlural}`;
      setError(apiError);
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoices(1);
  }, [filterStatus, filterType, variant]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchTerm !== '' || currentPage === 1) {
        fetchInvoices(1);
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleCreateInvoice = () => {
    setSelectedInvoice(null);
    setModalMode('add');
    setIsModalOpen(true);
  };

  const handleEditInvoice = async (invoice) => {
    try {
      setIsLoading(true);
      const detailUrl = isEstimates
        ? API_ENDPOINTS.ESTIMATES_DETAIL(invoice.id)
        : API_ENDPOINTS.INVOICES_DETAIL(invoice.id);
      const response = await axios.get(detailUrl, { headers: getAuthHeaders() });
      setSelectedInvoice(response.data);
      setModalMode('edit');
      setIsModalOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      `Failed to fetch ${noun} details`;
      setError(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewInvoice = async (invoice) => {
    try {
      setIsLoading(true);
      const detailUrl = isEstimates
        ? API_ENDPOINTS.ESTIMATES_DETAIL(invoice.id)
        : API_ENDPOINTS.INVOICES_DETAIL(invoice.id);
      const response = await axios.get(detailUrl, { headers: getAuthHeaders() });
      setSelectedInvoice(response.data);
      setIsDetailsModalOpen(true);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      `Failed to fetch ${noun} details`;
      setError(apiError);
    } finally {
      setIsLoading(false);
    }
  };

  const openDeleteConfirm = (invoice) => {
    setInvoiceToDelete(invoice);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteInvoice = async () => {
    if (!invoiceToDelete) return;
    try {
      const deleteUrl = isEstimates
        ? API_ENDPOINTS.ESTIMATES_DELETE(invoiceToDelete.id)
        : API_ENDPOINTS.INVOICES_DELETE(invoiceToDelete.id);
      await axios.delete(deleteUrl, { headers: getAuthHeaders() });
      setSuccessMessage(`${nounTitle} deleted successfully`);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchInvoices(currentPage);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      `Failed to delete ${noun}`;
      setError(apiError);
    } finally {
      setConfirmDeleteOpen(false);
      setInvoiceToDelete(null);
    }
  };

  const handleMarkAsPaid = async (invoice) => {
    try {
      const markPaidUrl = isEstimates
        ? API_ENDPOINTS.ESTIMATES_MARK_PAID(invoice.id)
        : API_ENDPOINTS.INVOICES_MARK_PAID(invoice.id);
      await axios.post(markPaidUrl, {}, { headers: getAuthHeaders() });
      setSuccessMessage(`${nounTitle} marked as paid`);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchInvoices(currentPage);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      `Failed to mark ${noun} as paid`;
      setError(apiError);
    }
  };

  const handleGenerateInvoiceNumber = async (invoice) => {
    try {
      const genUrl = isEstimates
        ? API_ENDPOINTS.ESTIMATES_GENERATE_NUMBER(invoice.id)
        : API_ENDPOINTS.INVOICES_GENERATE_NUMBER(invoice.id);
      const response = await axios.post(genUrl, {}, { headers: getAuthHeaders() });
      const generatedNo =
        response.data?.invoice_number ||
        response.data?.estimate_number ||
        response.data?.number;
      setSuccessMessage(
        isEstimates
          ? `Estimate number generated: ${generatedNo ?? ''}`
          : `Invoice number generated: ${generatedNo ?? ''}`
      );
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchInvoices(currentPage);
    } catch (error) {
      const apiError = error?.response?.data?.message || 
                      error?.response?.data?.detail || 
                      error?.message || 
                      isEstimates ? 'Failed to generate estimate number' : 'Failed to generate invoice number';
      setError(apiError);
    }
  };

  const handleModalSave = () => {
    setIsModalOpen(false);
    fetchInvoices(currentPage);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'sent':
        return <Send className="w-4 h-4" />;
      case 'draft':
        return <FileText className="w-4 h-4" />;
      case 'overdue':
        return <AlertCircle className="w-4 h-4" />;
      case 'cancelled':
        return <X className="w-4 h-4" />;
      default:
        return <Clock className="w-4 h-4" />;
    }
  };

  const filteredInvoices = invoices;

  return (
    <div className="space-y-6">
      {/* Notifications */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span>{error}</span>
          </div>
          <button onClick={() => setError('')} className="text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md flex items-center justify-between">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 mr-2" />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage('')} className="text-green-600 hover:text-green-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Total {nounTitle}s</p>
              <p className="text-2xl font-bold text-foreground">{totalCount}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Draft</p>
              <p className="text-2xl font-bold text-foreground">
                {invoices.filter(inv => inv.status === 'draft').length}
              </p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <FileText className="w-6 h-6 text-gray-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Sent</p>
              <p className="text-2xl font-bold text-foreground">
                {invoices.filter(inv => inv.status === 'sent').length}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Send className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Paid</p>
              <p className="text-2xl font-bold text-foreground">
                {invoices.filter(inv => inv.status === 'paid').length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={`Search ${nounPlural}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="domestic">Domestic</option>
            <option value="export">Export</option>
          </select>
        </div>
      </div>

      {/* Invoices Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="w-full overflow-x-auto">
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">Loading {nounPlural}...</p>
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-muted-foreground">No {nounPlural} found</p>
            </div>
          ) : (
            <>
              {/* Mobile / small-screen cards */}
              <div className="md:hidden space-y-4 p-4">
                {filteredInvoices.map((invoice) => (
                  <div key={invoice.id} className="border border-gray-200 rounded-lg p-4 shadow-sm">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{nounTitle}</p>
                        <p className="text-base font-semibold text-foreground">{invoice.invoice_number || 'N/A'}</p>
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                        {getStatusIcon(invoice.status)}
                        <span className="ml-1 capitalize">{invoice.status || 'N/A'}</span>
                      </span>
                    </div>
                    <div className="mt-3 space-y-1 text-sm text-foreground">
                      <p><span className="text-muted-foreground">Customer:</span> {invoice.customer_name || 'N/A'}</p>
                      <p><span className="text-muted-foreground">Amount:</span> ₹{parseFloat(invoice.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                      <p><span className="text-muted-foreground">Date:</span> {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}</p>
                      <p><span className="text-muted-foreground">Due:</span> {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}</p>
                      <p><span className="text-muted-foreground">Type:</span> {invoice.type || 'N/A'}</p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleViewInvoice(invoice)} title="View">
                        <Eye className="w-4 h-4 mr-2" /> View
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleEditInvoice(invoice)} title="Edit">
                        <Edit className="w-4 h-4 mr-2" /> Edit
                      </Button>
                      {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                        <Button variant="outline" size="sm" onClick={() => handleMarkAsPaid(invoice)} title="Mark as Paid">
                          <CheckCircle className="w-4 h-4 mr-2" /> Mark Paid
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => openDeleteConfirm(invoice)} title="Delete">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block">
                <table className="w-full min-w-[960px]">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{nounTitle}</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center mr-3">
                              <FileText className="w-5 h-5 text-white" />
                            </div>
                            <div>
                              <div className="text-sm font-medium text-gray-900">{invoice.invoice_number || 'N/A'}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{invoice.customer_name || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">₹{parseFloat(invoice.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.invoice_date ? new Date(invoice.invoice_date).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.due_date ? new Date(invoice.due_date).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(invoice.status)}`}>
                            {getStatusIcon(invoice.status)}
                            <span className="ml-1 capitalize">{invoice.status || 'N/A'}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-900 capitalize">{invoice.type || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex space-x-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleViewInvoice(invoice)}
                              title="View"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleEditInvoice(invoice)}
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={() => handleMarkAsPaid(invoice)}
                                title="Mark as Paid"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openDeleteConfirm(invoice)}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {(nextPage || prevPage) && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Showing page {currentPage} of {Math.ceil(totalCount / 10)}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchInvoices(currentPage - 1)}
                      disabled={!prevPage}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchInvoices(currentPage + 1)}
                      disabled={!nextPage}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {isModalOpen && (
        <InvoiceModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSave={handleModalSave}
          invoice={selectedInvoice}
          mode={modalMode}
          documentKind={isEstimates ? 'estimate' : 'invoice'}
        />
      )}

      {isDetailsModalOpen && selectedInvoice && (
        <InvoiceDetailsModal
          isOpen={isDetailsModalOpen}
          onClose={() => setIsDetailsModalOpen(false)}
          invoice={selectedInvoice}
          documentKind={isEstimates ? 'estimate' : 'invoice'}
        />
      )}

      {/* Delete Confirmation Modal */}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-foreground">Delete {nounTitle}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Are you sure you want to delete{' '}
                <span className="font-semibold text-foreground">
                  {invoiceToDelete?.invoice_number || `this ${noun}`}
                </span>
                ? This action cannot be undone.
              </p>
            </div>
            <div className="px-6 py-4 flex items-center justify-end gap-3">
              <Button variant="outline" onClick={() => { setConfirmDeleteOpen(false); setInvoiceToDelete(null); }}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteInvoice}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Invoices;

